import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import styles from './Investreal.module.css';

const RealEstateInvestment = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  
  const [submissionState, setSubmissionState] = useState({
    isSubmitting: false,
    isSuccess: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

 const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmissionState({ isSubmitting: true, isSuccess: false, error: null });

  try {
    const response = await fetch('https://pp-backend-1-sa8p.onrender.com/api/send-consultation', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    });
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to submit form');
    }

    setSubmissionState({ isSubmitting: false, isSuccess: true, error: null });
    
    // Reset form after successful submission
    setFormData({
      name: '',
      email: '',
      phone: '',
      interest: '',
      message: ''
    });
    
    // Hide success message after 5 seconds
    setTimeout(() => {
      setSubmissionState(prev => ({ ...prev, isSuccess: false }));
    }, 5000);
    
  } catch (error) {
    console.error('Submission error:', error);
    setSubmissionState({
      isSubmitting: false,
      isSuccess: false,
      error: error.message || 'Failed to submit. Please try again later or contact us directly.'
    });
  }
};

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Invest in Real Estate | Property Plateau</title>
        <meta name="description" content="High ROI real estate investments in India & abroad. Curated properties with data-driven insights for HNIs, NRIs & institutional investors." />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
        <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&family=Playfair+Display:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      <div className={styles.investmentPage}>
        {/* Success Message Modal */}
        {submissionState.isSuccess && (
          <motion.div 
            className={styles.successModal}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className={styles.successContent}>
              <svg className={styles.successIcon} viewBox="0 0 24 24">
                <path fill="currentColor" d="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" />
              </svg>
              <h3>Thank You!</h3>
              <p>Your consultation request has been submitted successfully.</p>
              <p>We will contact you shortly at the provided contact details.</p>
            </div>
          </motion.div>
        )}

        {/* Error Message */}
        {submissionState.error && (
          <motion.div 
            className={styles.errorMessage}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p>{submissionState.error}</p>
            <button 
              onClick={() => setSubmissionState(prev => ({ ...prev, error: null }))}
              className={styles.closeError}
            >
              &times;
            </button>
          </motion.div>
        )}

        {/* Hero Section */}
        <section className={styles.heroSection}>
          <div className={styles.container}>
            <motion.div 
              className={styles.heroContent}
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
     <h1 style={{ opacity: 1, transform: 'none', color: 'white' }}>
  Invest in Real Estate
</h1>

    <motion.h2 className={styles.heroSubtitle} variants={fadeInUp}>
                High ROI Real Estate Investments in India & Abroad
              </motion.h2>
              <motion.p className={styles.heroText} variants={fadeInUp}>
                Welcome to Property Plateau Realty - your trusted partner in real estate investment. 
                Whether you're looking to diversify your portfolio, generate passive income, or acquire 
                legacy assets, our expert team is here to guide you every step of the way.
              </motion.p>
              <motion.div className={styles.ctaButtons} variants={fadeInUp}>
               <a href="#consultation" className={styles.btn}>Book Consultation</a>
<a href="#focus-areas" className={styles.btn}>Explore Opportunities</a>

              </motion.div>
            </motion.div>
          </div>
          <div className={styles.heroOverlay}></div>
          <div className={styles.scrollIndicator}>
            <span></span>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className={`${styles.whyChooseUs} ${styles.sectionPadding}`}>
          <div className={styles.container}>
            <motion.h2 
              className={styles.sectionTitle}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              Why Property Plateau?
            </motion.h2>
            <div className={styles.featuresGrid}>
              {[
                {
                  icon: "📌",
                  title: "Curated Properties",
                  content: "We handpick real estate opportunities across India and select global locations, ensuring quality, compliance, and future value appreciation."
                },
                {
                  icon: "📊",
                  title: "Data-Driven Insights",
                  content: "From local market analytics to macroeconomic trends, we provide deep insights to help you make informed investment decisions."
                },
                {
                  icon: "🛡️",
                  title: "End-to-End Support",
                  content: "From due diligence to legal documentation, leasing, property management, and exit planning – we take care of everything so you can invest with confidence."
                }
              ].map((feature, index) => (
                <motion.div 
                  key={index}
                  className={styles.featureCard}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10 }}
                >
                  <div className={styles.featureIcon}>{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.content}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Investment Focus Areas */}
        <section id="focus-areas" className={`${styles.focusAreas} ${styles.sectionPadding} ${styles.bgLight}`}>
          <div className={styles.container}>
            <motion.h2 
              className={styles.sectionTitle}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              Our Investment Focus Areas
            </motion.h2>
            <div className={styles.focusGrid}>
              {[
                { icon: "🏠", title: "Residential", content: "Luxury villas, urban apartments, and high-rental yield homes" },
                { icon: "🏢", title: "Commercial", content: "Office spaces, co-working hubs, and retail opportunities" },
                { icon: "🌴", title: "Holiday Homes", content: "High-return Airbnb-style investments in tourist locations" },
                { icon: "📐", title: "Land & Plots", content: "Strategic land banks near developing corridors" },
                { icon: "✈️", title: "Global Investments", content: "Access opportunities in the UAE, UK, and Southeast Asia" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className={styles.focusCard}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                >
                  <div className={styles.focusIcon}>{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.content}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Investor Services */}
        <section className={`${styles.investorServices} ${styles.sectionPadding}`}>
          <div className={styles.container}>
            <motion.h2 
              className={styles.sectionTitle}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              Investor Services We Offer
            </motion.h2>
            <motion.div 
              className={styles.servicesList}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={staggerContainer}
            >
              <motion.ul className={styles.serviceColumn} variants={fadeInUp}>
                <li><span className={styles.checkIcon}>✓</span> Personalized Investment Advisory</li>
                <li><span className={styles.checkIcon}>✓</span> ROI Analysis & Risk Assessment</li>
                <li><span className={styles.checkIcon}>✓</span> Legal, Financial, and Tax Guidance</li>
              </motion.ul>
              <motion.ul className={styles.serviceColumn} variants={fadeInUp}>
                <li><span className={styles.checkIcon}>✓</span> Tenant Acquisition & Property Management</li>
                <li><span className={styles.checkIcon}>✓</span> Exit Strategies & Secondary Sales</li>
              </motion.ul>
            </motion.div>
          </div>
        </section>

        {/* Testimonial/Trust Section */}
        <section className={`${styles.trustSection} ${styles.sectionPadding} ${styles.bgLight}`}>
          <div className={styles.container}>
            <motion.h2 
              className={styles.sectionTitle}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              Trusted by HNIs, NRIs & Institutional Investors
            </motion.h2>
            <motion.div 
              className={styles.trustContent}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <p className={styles.trustText}>
                We've helped hundreds of investors build scalable, passive real estate portfolios. 
                From ₹50 lakhs to ₹50 crores – we structure opportunities based on your unique goals.
              </p>
              <motion.div 
                className={styles.trustBadges}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className={styles.badge}>RERA Certified</div>
                <div className={styles.badge}>10+ Years Experience</div>
                <div className={styles.badge}>500+ Happy Investors</div>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Consultation Form */}
        <section id="consultation" className={`${styles.consultationSection} ${styles.sectionPadding}`}>
          <div className={styles.container}>
            <motion.div 
              className={styles.consultationWrapper}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6 }}
            >
              <motion.div 
                className={styles.consultationContent}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className={styles.sectionTitle}>Book Your Free Consultation</h2>
                <p>
                  Let's talk about real estate. Share your goals, and we'll align the best-fit 
                  opportunities tailored for you.
                </p>
                <div className={styles.contactInfo}>
                  <div className={styles.contactItem}>
                    <span className={styles.contactIcon}>📱</span>
                    <a href="tel:+919156091640">+91 91560 91640</a>
                  </div>
                  <div className={styles.contactItem}>
                    <span className={styles.contactIcon}>✉️</span>
                    <a href="mailto:customerservice@propertyplateau.com">customerservice@propertyplateau.com</a>
                  </div>
                </div>
              </motion.div>
              <motion.div 
                className={styles.consultationForm}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <form onSubmit={handleSubmit}>
                  <div className={styles.formGroup}>
                    <input 
                      type="text" 
                      name="name"
                      placeholder="Full Name" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <input 
                      type="email" 
                      name="email"
                      placeholder="Email Address" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <input 
                      type="tel" 
                      name="phone"
                      placeholder="Phone Number" 
                      value={formData.phone}
                      onChange={handleChange}
                      required 
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <select 
                      name="interest"
                      value={formData.interest}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Investment Interest</option>
                      <option value="Residential">Residential</option>
                      <option value="Commercial">Commercial</option>
                      <option value="Holiday Homes">Holiday Homes</option>
                      <option value="Land & Plots">Land & Plots</option>
                      <option value="Global Investments">Global Investments</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <textarea 
                      name="message"
                      placeholder="Your Investment Goals (Optional)"
                      value={formData.message}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                  <button 
                    type="submit" 
                    className={`${styles.btn} ${styles.btnPrimary}`}
                    disabled={submissionState.isSubmitting}
                  >
                    {submissionState.isSubmitting ? (
                      <span className={styles.submitSpinner}></span>
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                </form>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className={`${styles.trustBadgesSection} ${styles.sectionPadding}`}>
          <div className={styles.container}>
            <motion.h3 
              className={styles.sectionSubtitle}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6 }}
            >
              Safe. Transparent. High-Growth.
            </motion.h3>
            <motion.p 
              className={styles.trustStatement}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              When you invest through Property Plateau, you're backed by:
            </motion.p>
            <motion.div 
              className={styles.badgesGrid}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={staggerContainer}
            >
              <motion.div 
                className={styles.badgeItem}
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <div className={styles.badgeIcon}>🔍</div>
                <p>100% transparency in pricing and documentation</p>
              </motion.div>
              <motion.div 
                className={styles.badgeItem}
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <div className={styles.badgeIcon}>⚖️</div>
                <p>RERA-compliant & legally verified properties</p>
              </motion.div>
              <motion.div 
                className={styles.badgeItem}
                variants={fadeInUp}
                whileHover={{ scale: 1.05 }}
              >
                <div className={styles.badgeIcon}>📈</div>
                <p>A vision to build generational wealth with real estate</p>
              </motion.div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default RealEstateInvestment;